/**
 * ARAMA ANAHTARLARI — sipariş/ürün/parça aramasının TEK kaynağı.
 *
 * NİÇİN TEK DOSYA: Bu projede arama üç ayrı yerde üç ayrı biçimde üretiliyordu
 * (`shopify/mappers.buildSearchTokens` Türkçe küçük harf, göç betiği ASCII'ye
 * katlanmış, `catalog/components` yine katlanmamış). Yazan ile okuyan farklı
 * biçim ürettiği için `array-contains` hata vermeden BOŞ dönüyordu. Artık hem
 * yazma hem sorgu tarafı bu dosyadan geçer; biçim değişirse ikisi birden değişir.
 *
 * Bu dosyayı `web/src/lib/searchKeys.ts` yeniden export eder — arayüz ile sunucu
 * AYNI kodu çalıştırır, kopya yoktur.
 *
 * Firestore'da tam metin arama YOKTUR. Seçilen yol: normalize edilmiş belirteç
 * dizisi (`searchTokens`) + `array-contains`. Gerekçe `buildOrderSearchTokens`
 * başlığında.
 */

/* ========================================================================
 * 1. TÜRKÇE KATLAMA
 * ===================================================================== */

/**
 * Türkçe'ye özgü harfler `toLowerCase()` ile doğru katlanmaz ve ICU verisine
 * bağlı `toLocaleLowerCase("tr-TR")` ortamdan ortama değişir. Bu yüzden eşleme
 * ELDE yazılıdır: sunucu, tarayıcı ve onarım betiği aynı sonucu üretir.
 *
 * ASCII'ye katlıyoruz çünkü kullanıcı klavyeden "atas" yazıp "Ataş"ı bulabilmeli.
 * Hem katlanmış hem katlanmamış biçimi SAKLAMAK gereksiz: sorgu da katlandığı
 * için tek biçim iki yönü de karşılar.
 */
const TR_FOLD: Record<string, string> = {
  "İ": "i", "I": "i", "ı": "i",
  "Ş": "s", "ş": "s",
  "Ğ": "g", "ğ": "g",
  "Ü": "u", "ü": "u",
  "Ö": "o", "ö": "o",
  "Ç": "c", "ç": "c",
  // Türkçe'de düzeltme işareti (kâğıt, hâlâ) ve göç verisindeki bozuk kayıtlar.
  "Â": "a", "â": "a", "Î": "i", "î": "i", "Û": "u", "û": "u",
};

/**
 * Metni arama anahtarı alfabesine indirger: küçük harf + ASCII.
 *
 * Sıra önemli: Türkçe harfler ÖNCE elle katlanır (aksi hâlde "I" İngilizce
 * kuralıyla "i" olur ve "Işıl" ile "İsil" ayrışır), sonra kalan aksanlar
 * Unicode ayrıştırmasıyla (NFD) düşürülür.
 */
export function foldTr(input: string): string {
  let out = "";
  for (const ch of input) out += TR_FOLD[ch] ?? ch;
  return out
    .toLowerCase()
    .normalize("NFD")
    // Birleşen aksan işaretleri (NFD sonrası) — kaynakta görünmez karakter
    // bırakmamak için aralık yerine Unicode özelliği kullanılır.
    .replace(/\p{M}/gu, "");
}

/* ========================================================================
 * 2. TELEFON
 * ===================================================================== */

/**
 * Telefonu tek biçime indirger: 10 haneli abone numarası ("5548621121").
 *
 * Aynı numara veride üç biçimde duruyor ("05548621121", "5337772173",
 * "+90 554 862 11 21"). Kullanıcı hangisini yazarsa yazsın bulmalı; bu yüzden
 * saklarken de sorarken de bu fonksiyondan geçer.
 *
 * Türkiye dışı numaralar için (10 haneye inmiyorsa) ham rakamlar döner —
 * sessizce atmak yerine bulunabilir kalması yeğdir.
 */
export function normalizePhone(input: string): string {
  const digits = input.replace(/\D+/g, "");
  if (digits.length < 7) return "";
  let d = digits;
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("90") && d.length === 12) d = d.slice(2);
  if (d.startsWith("0") && d.length === 11) d = d.slice(1);
  return d;
}

/** Girdi telefon numarası "gibi mi" — sorgu tarafında biçim tahmini için. */
function looksLikePhone(input: string): boolean {
  const compact = input.replace(/[\s()\-.+]/g, "");
  if (!/^\d+$/.test(compact)) return false;
  return compact.length >= 10 && compact.length <= 13;
}

/* ========================================================================
 * 3. BELİRTEÇ ÜRETİMİ
 * ===================================================================== */

/** Anahtar sayılan en kısa belirteç. Tek harf her siparişi getirirdi. */
const MIN_TOKEN = 2;
/** Ön ek üretiminin başladığı uzunluk — "ata" ile "Ataş" bulunur. */
const MIN_PREFIX = 3;
/** Ön ek üretilen en uzun kelime; daha uzunları indeksi şişirir, faydası yok. */
const MAX_PREFIX_SOURCE = 24;

/**
 * Firestore sınırı doküman başına 40.000 indeks girdisidir; dizinin her elemanı
 * bağlı olduğu her indekste bir girdi üretir. 200 belirteç bu sınırın çok
 * altındadır ve 1 MiB doküman sınırına da ~4 KB ile katkı verir.
 *
 * Sınır aşılırsa SESSİZCE kırpmayız: `dropped` sayısı çağırana döner, yazan
 * taraf uyarı kaydeder (CONTRACT §2 — sessiz atlama yok).
 */
export const MAX_SEARCH_TOKENS = 200;

/** Metni kelimelere ayırır: harf/rakam dışı her şey sınırdır. */
function words(input: string): string[] {
  return foldTr(input)
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= MIN_TOKEN);
}

/**
 * Harften oluşan kelimeler için ön ekleri üretir ("ataş" → ata).
 *
 * Rakam içeren belirteçlere ön ek ÜRETİLMEZ: sipariş numarası, telefon ve takip
 * numarası tam eşleşmelidir. "1" ön eki 14.616 siparişin yarısını getirirdi.
 */
function addPrefixes(token: string, sink: Set<string>): void {
  if (!/^[a-z]+$/.test(token)) return;
  if (token.length <= MIN_PREFIX || token.length > MAX_PREFIX_SOURCE) return;
  for (let i = MIN_PREFIX; i < token.length; i++) sink.add(token.slice(0, i));
}

/** Serbest metin alanı (ad, şehir, ürün başlığı) — kelimeler + ön ekleri. */
function addText(value: unknown, sink: Set<string>): void {
  if (typeof value !== "string" || value === "") return;
  for (const w of words(value)) {
    sink.add(w);
    addPrefixes(w, sink);
  }
}

/**
 * Kod alanı (sipariş no, takip no, SKU, Shopify kimliği) anahtarları.
 *
 * ★ BU FONKSİYON HEM YAZARKEN HEM ARARKEN ÇAĞRILIR. Simetri şart: sipariş
 * numarasını yazarken üretilen küme ile kullanıcı aynı numarayı yazdığında
 * üretilen küme AYNI olmalı, yoksa `array-contains` hata vermeden boş döner —
 * bu projenin baskın hata sınıfı tam olarak budur.
 *
 * Üretilen biçimler:
 *   "#DMNTH1028TR" → "dmnth1028tr" (bitişik), "dmnth", "1028", "tr" (parçalar)
 * Böylece kullanıcı "#"siz, küçük harfle ya da sadece rakamla da bulur.
 */
function codeKeys(raw: string): string[] {
  const trimmed = raw.trim();
  if (trimmed === "") return [];
  const out = new Set<string>();
  const joined = foldTr(trimmed).replace(/[^a-z0-9]+/g, "");
  if (joined.length >= MIN_TOKEN) out.add(joined);
  for (const w of words(trimmed)) out.add(w);

  /**
   * Harf/rakam sınırından bölme YALNIZCA sipariş numarası kalıbında yapılır:
   * kısa harf öneki + rakam çekirdeği + kısa harf soneki ("dmnth1028tr",
   * "ty11494130904"). Kullanıcı bu numaraları çoğunlukla sadece rakamıyla
   * söylüyor ("1028 nolu sipariş").
   *
   * Her koda uygulanmaz: UPS takip numarası "1zj37v656800000039" bölünürse
   * "37", "zj" gibi anlamsız belirteçler doğar; e-posta kullanıcı adı
   * "ezgiakdas46" bölünürse sorgu tarafı belgede olmayan "ezgiakdas" anahtarını
   * ŞART koşar ve arama sessizce boş döner.
   */
  const m = /^([a-z]{1,6})(\d{2,})([a-z]{0,4})$/.exec(joined);
  if (m) {
    for (const run of [m[1], m[2], m[3]]) {
      if (run !== undefined && run.length >= MIN_TOKEN) out.add(run);
    }
  }
  return [...out];
}

/** Kod alanını dizine ekler. */
function addCode(value: unknown, sink: Set<string>): void {
  if (value == null) return;
  for (const k of codeKeys(String(value))) sink.add(k);
}

/**
 * E-posta anahtarları — tamamı + kullanıcı adı + alan adı.
 *
 * Kullanıcı adı iki biçimde yazılır ("ezgi_akdas46" ve "ezgiakdas46") çünkü
 * kullanıcı alt çizgiyi yazmadan da arıyor. `codeKeys` ile aynı simetri kuralı
 * geçerlidir: bu fonksiyon da hem yazma hem sorgu tarafında çağrılır.
 */
function emailKeys(raw: string): string[] {
  const mail = foldTr(raw.trim());
  if (!mail.includes("@")) return [];
  const out = new Set<string>();
  out.add(mail);
  const at = mail.indexOf("@");
  const local = mail.slice(0, at);
  const domain = mail.slice(at + 1);
  for (const part of [local, domain]) {
    if (part.length >= MIN_TOKEN) out.add(part);
    const joined = part.replace(/[^a-z0-9]+/g, "");
    if (joined.length >= MIN_TOKEN) out.add(joined);
    for (const w of words(part)) out.add(w);
  }
  return [...out];
}

/** E-postayı dizine ekler. */
function addEmail(value: unknown, sink: Set<string>): void {
  if (typeof value !== "string" || !value.includes("@")) return;
  for (const k of emailKeys(value)) sink.add(k);
}

/** Telefon: yalnızca 10 haneli kanonik biçim. */
function addPhone(value: unknown, sink: Set<string>): void {
  if (typeof value !== "string" && typeof value !== "number") return;
  const p = normalizePhone(String(value));
  if (p.length >= MIN_TOKEN) sink.add(p);
}

/* ========================================================================
 * 4. SİPARİŞ BELİRTEÇLERİ
 * ===================================================================== */

/** `buildOrderSearchTokens` girdisi — Firestore doküman biçimi (payload DEĞİL). */
export interface OrderSearchSource {
  id?: string | null;
  orderName?: string | null;
  shopifyOrderId?: string | null;
  legacyWooOrderId?: string | null;
  customerFullName?: string | null;
  customer?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  /**
   * Teslimat adı fatura adından FARKLI olabilir (hediye gönderimi, kurumsal
   * fatura + şahsa teslim). Ölçüldü: 14.622 siparişin 1.252'sinde teslimat adı
   * fatura adından farklı ve bunların 1.169'u ALICI ADIYLA HİÇ BULUNAMIYORDU —
   * çünkü yalnızca `city`/`phone`/`company` indeksleniyordu. Müşteri hizmetleri
   * kargoyu alan kişinin adıyla arıyor; o ad indekste yoksa arama sessizce boş
   * döner ve kullanıcı siparişin göç etmediğini sanır.
   */
  shippingAddress?: {
    firstName?: string | null;
    lastName?: string | null;
    city?: string | null;
    phone?: string | null;
    company?: string | null;
  } | null;
  billingAddress?: {
    firstName?: string | null;
    lastName?: string | null;
    city?: string | null;
    phone?: string | null;
    company?: string | null;
  } | null;
  /** Shopify/Trendyol kargo aynası */
  tracking?: { number?: string | null } | null;
  /** ERP'den elle girilen kargo — ALAN ADI FARKLI, ikisi de taranmalı */
  trackingInfo?: { trackingNumber?: string | null } | null;
  trendyol?: { orderNumber?: string | null; packageId?: string | null } | null;
}

export interface SearchTokensResult {
  tokens: string[];
  /** Sınır aşıldığı için yazılamayan belirteç sayısı — sessizce kırpılmaz. */
  dropped: number;
}

/**
 * Siparişin arama belirteçlerini üretir.
 *
 * ── NİÇİN `array-contains`, ÖN EK ARAMASI DEĞİL ────────────────────────────
 * Önceki çözüm tek alan üzerinde `[q, q+]` aralık sorgusuydu. Aralık
 * sorgusu ancak alanın BAŞINDAN eşleşir: "Ezgi Ataş" siparişini "Ataş" yazarak
 * bulmak MÜMKÜN DEĞİLDİ; kullanıcının şikâyeti tam olarak buydu. Ayrıca tek
 * alan tarandığı için takip numarası, e-posta ve telefon hiç aranamıyordu.
 * Belirteç dizisi kelimenin yerinden bağımsız eşleşir ve tüm alanları tek
 * indekste toplar; `orders` için gereken bileşik indeks zaten mevcuttur.
 *
 * PII (e-posta/telefon) belirteçlere GİRER — bu veridir. Log'a asla yazılmaz
 * (CONTRACT §5).
 */
export function buildOrderSearchTokens(order: OrderSearchSource): SearchTokensResult {
  const sink = new Set<string>();

  addCode(order.id, sink);
  addCode(order.orderName, sink);
  addCode(order.shopifyOrderId, sink);
  addCode(order.legacyWooOrderId, sink);

  addText(order.customerFullName, sink);
  addText(order.customer?.firstName, sink);
  addText(order.customer?.lastName, sink);
  addEmail(order.customer?.email, sink);
  addPhone(order.customer?.phone, sink);

  // Kargoyu teslim alan kişi — fatura adından farklı olduğunda tek bulunma yolu.
  addText(order.shippingAddress?.firstName, sink);
  addText(order.shippingAddress?.lastName, sink);
  addText(order.billingAddress?.firstName, sink);
  addText(order.billingAddress?.lastName, sink);

  addText(order.shippingAddress?.city, sink);
  addText(order.billingAddress?.city, sink);
  addText(order.shippingAddress?.company, sink);
  addPhone(order.shippingAddress?.phone, sink);
  addPhone(order.billingAddress?.phone, sink);

  // İki ayrı takip alanı gerçekten VAR: webhook/Trendyol `tracking.number`,
  // ERP ekranı `trackingInfo.trackingNumber` yazıyor. Birini taramak siparişin
  // yarısını kaybettiriyordu.
  addCode(order.tracking?.number, sink);
  addCode(order.trackingInfo?.trackingNumber, sink);

  addCode(order.trendyol?.orderNumber, sink);
  addCode(order.trendyol?.packageId, sink);

  const all = [...sink];
  return { tokens: all.slice(0, MAX_SEARCH_TOKENS), dropped: Math.max(0, all.length - MAX_SEARCH_TOKENS) };
}

/* ========================================================================
 * 5. KATALOG BELİRTEÇLERİ
 * ===================================================================== */

export interface CatalogSearchSource {
  id?: string | null;
  title?: string | null;
  name?: string | null;
  sku?: string | null;
  barcode?: string | null;
  categoryName?: string | null;
}

/** Ürün/parça belirteçleri — sipariş ile AYNI katlamayı kullanır. */
export function buildCatalogSearchTokens(item: CatalogSearchSource): SearchTokensResult {
  const sink = new Set<string>();
  addText(item.title, sink);
  addText(item.name, sink);
  addText(item.categoryName, sink);
  addCode(item.sku, sink);
  addCode(item.barcode, sink);
  const all = [...sink];
  return { tokens: all.slice(0, MAX_SEARCH_TOKENS), dropped: Math.max(0, all.length - MAX_SEARCH_TOKENS) };
}

/* ========================================================================
 * 6. SORGU TARAFI
 * ===================================================================== */

/**
 * Kullanıcının yazdığını belirteç anahtarlarına çevirir.
 *
 * Dönen dizi UZUNLUKTAN KISAYA sıralıdır: ilk anahtar en seçici olandır ve
 * Firestore sorgusunda o kullanılır; kalanlar istemci tarafında AND koşulu
 * olarak süzülür (Firestore iki `array-contains`i aynı sorguda kabul etmez).
 *
 * Telefon özel durumdur: "0554 862 11 21" kelimelere bölünürse hiçbir şey
 * bulmaz, bu yüzden önce bütün olarak telefon mu diye bakılır.
 */
export function buildQueryKeys(input: string): string[] {
  const q = input.trim();
  if (q === "") return [];

  if (looksLikePhone(q)) {
    const p = normalizePhone(q);
    if (p.length >= MIN_TOKEN) return [p];
  }

  const sink = new Set<string>();

  if (q.includes("@")) {
    // E-posta bütün olarak yorumlanır; parçalarına ayırmak "com" gibi anahtar
    // üretir ve AND koşulunu boşuna daraltır.
    for (const k of emailKeys(q)) sink.add(k);
  } else if (!/\s/.test(q)) {
    // Boşluksuz girdi bir koddur (sipariş no, takip no, SKU, Shopify kimliği).
    // Yazma tarafıyla AYNI fonksiyondan geçer — simetri buradan gelir.
    for (const k of codeKeys(q)) sink.add(k);
  } else {
    for (const w of words(q)) sink.add(w);
  }

  return [...sink].sort((a, b) => b.length - a.length || a.localeCompare(b));
}

/**
 * Belge gerçekten TÜM anahtarları taşıyor mu?
 *
 * Firestore tek `array-contains` ile yalnızca ilk anahtarı süzer; "Ezgi Ataş"
 * yazan kullanıcı "Ezgi Demir"i sonuçta görmemeli. Bu kontrol istemcide yapılır.
 */
export function matchesAllKeys(tokens: readonly string[] | null | undefined, keys: readonly string[]): boolean {
  if (!tokens || tokens.length === 0) return false;
  const set = new Set(tokens);
  return keys.every((k) => set.has(k));
}

/* ========================================================================
 * 6b. ANAHTAR SEÇİMİ VE BOŞ SONUCUN TEŞHİSİ
 * ===================================================================== */

/** Bir anahtarın koleksiyonlardaki toplam eşleşme sayısı. */
export type KeyCounts = Readonly<Record<string, number>>;

/**
 * Firestore'da sorgulanacak anahtarı seçer: EN NADİR olan.
 *
 * ── NİÇİN "EN UZUN" DEĞİL ───────────────────────────────────────────────────
 * Önceki seçim uzunluğa göreydi ve uzunluk seçicilik DEĞİLDİR. Ölçüldü:
 * "fatma korun" aramasında iki anahtar da 5 harf; alfabetik olarak "fatma"
 * seçiliyordu. "fatma" 118 siparişte geçer, "korun" 0 siparişte. Sorgu
 * `array-contains "fatma"` + `orderBy createdAt desc` + `limit 60` ile EN YENİ
 * 60 "fatma" siparişini çekip istemcide "korun" şartıyla eliyordu; geriye kalan
 * 58 "fatma" siparişi sorgunun penceresine HİÇ girmiyordu. Sonuç boş dönüyordu
 * ve ekran bunu "arama bulamadı" diye sunuyordu.
 *
 * En nadir anahtarla sorgulandığında pencere sorunu büyük ölçüde ortadan kalkar
 * (0 eşleşen anahtar varsa sorgu zaten anında boş döner) ve daha önemlisi
 * sayımlar boş sonucun SEBEBİNİ ölçülebilir kılar — bkz. `classifyEmptyResult`.
 */
export function pickRarestKey(keys: readonly string[], counts: KeyCounts): string | null {
  if (keys.length === 0) return null;
  let best = keys[0] as string;
  let bestCount = counts[best] ?? Number.POSITIVE_INFINITY;
  for (const k of keys.slice(1)) {
    const c = counts[k] ?? Number.POSITIVE_INFINITY;
    // Eşitlikte uzun anahtar yeğlenir: aynı sayıda eşleşiyorsa uzun olan
    // kavramsal olarak daha dardır ve sıralamayı belirlenimci kılar.
    if (c < bestCount || (c === bestCount && k.length > best.length)) {
      best = k;
      bestCount = c;
    }
  }
  return best;
}

/**
 * Boş sonucun SEBEBİ — "kayıt yok" ile "arama bulamadı" ayrımı.
 *
 * ── NİÇİN GEREKLİ ───────────────────────────────────────────────────────────
 * Eski ekran her boş sonuçta "Arama TAM KELİME eşleşir…" yazıyordu; yani
 * kullanıcıya örtük olarak "yanlış yazdın" diyordu. Gerçekte "fatma korun"
 * siparişi eski sistemde VAR (Woo #77552) ama yeni sisteme HİÇ göç etmemiş.
 * Kullanıcı aramayı suçlayıp dakikalarca farklı yazımlar denedi; oysa kayıt
 * ortada yoktu. Bu ayrım tahmin değil ÖLÇÜMDÜR: `count()` toplaması bir
 * terimin indekste kaç kayıtta geçtiğini kesin söyler.
 *
 *   absent        → terim hiçbir kayıtta geçmiyor. Kayıt sistemde YOK.
 *                   (yazım hatası da olabilir ama "arama kısıtı" DEĞİL)
 *   noCombination → terimlerin her biri ayrı ayrı var, birlikte taşıyan yok.
 *   windowCapped  → sunucu penceresi doldu; kesin konuşulamaz, daraltmak gerek.
 */
export type EmptyVerdict =
  | { kind: "absent"; absentKeys: string[] }
  | { kind: "noCombination"; counts: KeyCounts }
  | { kind: "windowCapped" };

export function classifyEmptyResult(
  keys: readonly string[],
  counts: KeyCounts,
  capped: boolean,
): EmptyVerdict {
  // Sayımı bilinmeyen anahtar varsa "yok" diyemeyiz — ölçmediğimizi iddia etmeyiz.
  const absentKeys = keys.filter((k) => counts[k] === 0);
  if (absentKeys.length > 0) return { kind: "absent", absentKeys };

  /**
   * Pencere dolduysa eleme istemcide yapıldığı için "birlikte taşıyan yok"
   * DENEMEZ: görülmeyen kayıtlarda olabilir. Sıra önemli — önce sınır.
   */
  if (capped) return { kind: "windowCapped" };

  return { kind: "noCombination", counts };
}

/* ========================================================================
 * 7. "NEYLE BULUNDU" AÇIKLAMASI
 * ===================================================================== */

export type MatchReason =
  | "orderNo"
  | "customer"
  | "recipient"
  | "phone"
  | "email"
  | "tracking"
  | "trendyol"
  | "city"
  | "shopifyId";

const REASON_LABEL: Record<MatchReason, string> = {
  orderNo: "sipariş no",
  customer: "müşteri adı",
  recipient: "teslimat adı",
  phone: "telefon",
  email: "e-posta",
  tracking: "takip no",
  trendyol: "Trendyol no",
  city: "şehir",
  shopifyId: "Shopify kimliği",
};

/**
 * Sonucun HANGİ alan yüzünden geldiğini bulur.
 *
 * Kullanıcı "neden bu sipariş çıktı?" diye sormamalı; eski ekran yalnızca listeyi
 * gösteriyordu. Alan alan yeniden belirteç üretip anahtarla karşılaştırıyoruz —
 * tahmin değil, ölçüm.
 */
export function explainOrderMatch(order: OrderSearchSource, keys: readonly string[]): string[] {
  if (keys.length === 0) return [];
  const groups: Array<[MatchReason, () => Set<string>]> = [
    ["orderNo", () => { const s = new Set<string>(); addCode(order.orderName, s); addCode(order.id, s); return s; }],
    ["customer", () => {
      const s = new Set<string>();
      addText(order.customerFullName, s);
      addText(order.customer?.firstName, s);
      addText(order.customer?.lastName, s);
      return s;
    }],
    ["recipient", () => {
      const s = new Set<string>();
      addText(order.shippingAddress?.firstName, s);
      addText(order.shippingAddress?.lastName, s);
      addText(order.billingAddress?.firstName, s);
      addText(order.billingAddress?.lastName, s);
      return s;
    }],
    ["tracking", () => {
      const s = new Set<string>();
      addCode(order.tracking?.number, s);
      addCode(order.trackingInfo?.trackingNumber, s);
      return s;
    }],
    ["trendyol", () => {
      const s = new Set<string>();
      addCode(order.trendyol?.orderNumber, s);
      addCode(order.trendyol?.packageId, s);
      return s;
    }],
    ["phone", () => {
      const s = new Set<string>();
      addPhone(order.customer?.phone, s);
      addPhone(order.shippingAddress?.phone, s);
      addPhone(order.billingAddress?.phone, s);
      return s;
    }],
    ["email", () => { const s = new Set<string>(); addEmail(order.customer?.email, s); return s; }],
    ["city", () => {
      const s = new Set<string>();
      addText(order.shippingAddress?.city, s);
      addText(order.billingAddress?.city, s);
      return s;
    }],
    ["shopifyId", () => { const s = new Set<string>(); addCode(order.shopifyOrderId, s); return s; }],
  ];

  const reasons: string[] = [];
  for (const [reason, build] of groups) {
    const set = build();
    if (keys.some((k) => set.has(k))) reasons.push(REASON_LABEL[reason]);
  }
  return reasons;
}
